import { BaseQLClient } from '@adornis/baseql/client/baseqlClient.js';
import { initApp } from '@adornis/functional-lit/init-app.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { authenticationGuard } from '@adornis/user-forms/authentication-guard.js';
import { subscribeCurrentUser } from '@adornis/users/client/currentUser.js';
import { logout } from '@adornis/users/client/loginFunctions.js';
import { html } from 'lit';
import '../entities/index.js';
import logoPath from './assets/sourcewerbe_logo.svg';
import { AnsweringDashboard } from './components/AnsweringDashboard.js';
import { BadcontactDashboard } from './components/BadcontactDashboard.js';
import { CommentDashboard } from './components/CommentDashboard.js';
import { ContactsDashboard } from './components/ContactsDashboard.js';
import { DashboardHome } from './components/DashboardHome.js';
import { IntercomDashboard } from './components/IntercomDashboard.js';
import { KnowledgebaseDashboard } from './components/KnowledgebaseDashboard.js';
import { Kpis } from './components/Kpis.js';
import { MailDashboard } from './components/MailDashboard.js';
import { OdooTicketDashboard } from './components/OdooTicketDashboard.js';
import { ProcessDashboard } from './components/ProcessDashboard.js';
import { TaskqueueDashboard } from './components/TaskqueueDashboard.js';
import './tailwind.css' with { type: 'tailwind' };
import { functionalLogin } from '@adornis/user-forms/function-login.js';

BaseQLClient.initDefault();
subscribeCurrentUser();

// Simple layout function without stateful wrapper
const AppLayout = ({ children }: { children: unknown }) => {
  return html`
    <div class="flex flex-col h-full bg-base-100">
      <!-- Header similar to SOURCE website -->
      <header class="bg-primary text-primary-content py-3 px-4 shadow-md">
        <div class="container mx-auto flex justify-between items-center">
          <!-- Logo on the left -->
          <a href="/" class="hover:opacity-90 transition-opacity">
            <img src="${logoPath}" alt="SOURCE Werbe" class="h-8" />
          </a>

          <!-- Navigation icons with tooltips -->
          <div class="flex gap-2 items-center">
            <div class="tooltip tooltip-bottom" data-tip="Home">
              <a
                href="/"
                class="btn btn-circle ${router.currentRoute.value.route.name === 'home'
                  ? 'btn-accent text-accent-content'
                  : 'btn-ghost text-primary-content hover:bg-primary-focus'}"
                aria-label="Home"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
              </a>
            </div>

            <div class="tooltip tooltip-bottom" data-tip="Mails">
              <a
                href="/mails"
                class="btn btn-circle ${router.currentRoute.value.route.name === 'mails'
                  ? 'btn-accent text-accent-content'
                  : 'btn-ghost text-primary-content hover:bg-primary-focus'}"
                aria-label="Mails"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                  />
                </svg>
              </a>
            </div>

            <div class="tooltip tooltip-bottom" data-tip="Intercom">
              <a
                href="/intercom"
                class="btn btn-circle ${router.currentRoute.value.route.name === 'intercom'
                  ? 'btn-accent text-accent-content'
                  : 'btn-ghost text-primary-content hover:bg-primary-focus'}"
                aria-label="Intercom"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
                  />
                </svg>
              </a>
            </div>

            <div class="tooltip tooltip-bottom" data-tip="Contacts">
              <a
                href="/contacts"
                class="btn btn-circle ${router.currentRoute.value.route.name === 'contacts'
                  ? 'btn-accent text-accent-content'
                  : 'btn-ghost text-primary-content hover:bg-primary-focus'}"
                aria-label="Contacts"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </a>
            </div>

            <div class="tooltip tooltip-bottom" data-tip="All answers">
              <a
                href="/answering"
                class="btn btn-circle ${router.currentRoute.value.route.name === 'answering'
                  ? 'btn-accent text-accent-content'
                  : 'btn-ghost text-primary-content hover:bg-primary-focus'}"
                aria-label="Processes"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M11.983 13.977a1.994 1.994 0 100-3.988 1.994 1.994 0 000 3.988zM20.94 12.997a8.063 8.063 0 00.061-1.002 8.063 8.063 0 00-.061-1.002l2.122-1.654a.5.5 0 00.121-.639l-2-3.464a.5.5 0 00-.605-.222l-2.5 1a7.94 7.94 0 00-1.733-1.002l-.375-2.65A.5.5 0 0015.5 2h-4a.5.5 0 00-.493.424l-.375 2.65a7.94 7.94 0 00-1.733 1.002l-2.5-1a.5.5 0 00-.605.222l-2 3.464a.5.5 0 00.121.639l2.122 1.654a8.063 8.063 0 000 2.004L3.514 14.65a.5.5 0 00-.121.639l2 3.464a.5.5 0 00.605.222l2.5-1a7.94 7.94 0 001.733 1.002l.375 2.65A.5.5 0 0011.5 22h4a.5.5 0 00.493-.424l.375-2.65a7.94 7.94 0 001.733-1.002l2.5 1a.5.5 0 00.605-.222l2-3.464a.5.5 0 00-.121-.639l-2.122-1.654z"
                  />
                </svg>
              </a>
            </div>

            <div class="tooltip tooltip-bottom" data-tip="KPIs">
              <a
                href="/kpis"
                class="btn btn-circle ${router.currentRoute.value.route.name === 'kpis'
                  ? 'btn-accent text-accent-content'
                  : 'btn-ghost text-primary-content hover:bg-primary-focus'}"
                aria-label="Processes"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M11.983 13.977a1.994 1.994 0 100-3.988 1.994 1.994 0 000 3.988zM20.94 12.997a8.063 8.063 0 00.061-1.002 8.063 8.063 0 00-.061-1.002l2.122-1.654a.5.5 0 00.121-.639l-2-3.464a.5.5 0 00-.605-.222l-2.5 1a7.94 7.94 0 00-1.733-1.002l-.375-2.65A.5.5 0 0015.5 2h-4a.5.5 0 00-.493.424l-.375 2.65a7.94 7.94 0 00-1.733 1.002l-2.5-1a.5.5 0 00-.605.222l-2 3.464a.5.5 0 00.121.639l2.122 1.654a8.063 8.063 0 000 2.004L3.514 14.65a.5.5 0 00-.121.639l2 3.464a.5.5 0 00.605.222l2.5-1a7.94 7.94 0 001.733 1.002l.375 2.65A.5.5 0 0011.5 22h4a.5.5 0 00.493-.424l.375-2.65a7.94 7.94 0 001.733-1.002l2.5 1a.5.5 0 00.605-.222l2-3.464a.5.5 0 00-.121-.639l-2.122-1.654z"
                  />
                </svg>
              </a>
            </div>
            <button
              class="btn btn-sm btn-ghost text-primary-content hover:bg-primary-focus"
              @click=${logout}
              aria-label="Logout"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main class="flex-1 min-h-0 container mx-auto py-8 px-4 overflow-y-auto">${children}</main>
    </div>
  `;
};

const router = makeRouter({
  home: {
    path: '/',
    render: () =>
      AppLayout({
        children: DashboardHome(),
      }),
  },
  mails: {
    path: '/mails',
    render: () =>
      AppLayout({
        children: MailDashboard(),
      }),
  },
  'odoo-tickets': {
    path: '/odoo-tickets',
    render: () =>
      AppLayout({
        children: OdooTicketDashboard(),
      }),
  },
  intercom: {
    path: '/intercom',
    render: () =>
      AppLayout({
        children: IntercomDashboard(),
      }),
  },
  contacts: {
    path: '/contacts',
    render: () =>
      AppLayout({
        children: ContactsDashboard(),
      }),
  },
  processes: {
    path: '/processes',
    render: () =>
      AppLayout({
        children: ProcessDashboard(),
      }),
  },
  answering: {
    path: '/answering',
    render: () =>
      AppLayout({
        children: AnsweringDashboard(),
      }),
  },
  taskqueue: {
    path: '/taskqueue',
    render: () =>
      AppLayout({
        children: TaskqueueDashboard(),
      }),
  },
  kpis: {
    path: '/kpis',
    render: () =>
      AppLayout({
        children: Kpis(),
      }),
  },
  comments: {
    path: '/comments',
    render: () =>
      AppLayout({
        children: CommentDashboard(),
      }),
  },
  knowledgebase: {
    path: '/knowledgebase',
    render: () =>
      AppLayout({
        children: KnowledgebaseDashboard(),
      }),
  },
  badcontact: {
    path: '/badcontact',
    render: () =>
      AppLayout({
        children: BadcontactDashboard(),
      }),
  },
}).init();

initApp(() => authenticationGuard(stateful(() => html`${router.outlet.value}`), functionalLogin({})));
